// Project-scoped service accounts (v103). Project owners (project_admin
// or org_admin) create these for CI / pipeline automation.

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Key, Bot, AlertTriangle, Copy, Check } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { useProjects } from '../api/client';
import {
  useServiceAccounts,
  useSATokens,
  createSA,
  deleteSA,
  createSAToken,
  deleteSAToken,
  type CreatedSAToken,
} from '../api/serviceAccounts';

const SCOPES = ['docker:pull', 'docker:push', 'read', 'write'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

export function ServiceAccountsPage() {
  const { data: projects } = useProjects();
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') ?? '';
  const setProject = (id: string) => {
    if (id) setParams({ project: id });
    else setParams({});
  };
  const { data: sas, mutate } = useServiceAccounts(projectId || undefined);

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expandedSA, setExpandedSA] = useState<string | null>(null);

  const onCreate = async () => {
    if (!projectId || !name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createSA(projectId, { name: name.trim(), description: desc.trim() });
      setName(''); setDesc('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDeleteSA = async (id: string) => {
    if (!projectId) return;
    if (!confirm('Delete this service account? All its tokens are revoked immediately.')) return;
    await deleteSA(projectId, id);
    await mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">Workflow · automation</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Service accounts</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          Project-scoped non-human identities for CI / pipelines. Each service
          account belongs to one project; its tokens authenticate via the
          same HTTP-Basic / Bearer flow as user tokens but stay confined to
          the project they were created in.
        </p>
      </div>

      <Card>
        <CardHeader title="Project" description="Pick the project you want to manage service accounts for. Requires project_admin or org_admin." />
        <CardBody>
          <select
            className={selectClass + ' w-full max-w-md'}
            value={projectId}
            onChange={(e) => setProject(e.target.value)}
          >
            <option value="">— select a project —</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </CardBody>
      </Card>

      {projectId && (
        <>
          <Card>
            <CardHeader title="Create service account" description="Names are unique within the project." />
            <CardBody className="space-y-3">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="name (e.g. ci-publisher)" />
              <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="description (optional)" />
              {err && <p className="text-danger font-mono text-sm">{err}</p>}
            </CardBody>
            <CardFooter className="flex justify-end">
              <Button variant="primary" disabled={busy || !name.trim()} onClick={onCreate}>
                <Plus className="h-4 w-4" />
                Create
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader title={`Service accounts (${sas?.length ?? 0})`} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                    <th className="px-5 py-3 font-medium text-fg-muted">Description</th>
                    <th className="px-5 py-3 font-medium text-fg-muted">Tokens</th>
                    <th className="px-5 py-3 font-medium text-fg-muted">Created</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {sas?.length ? (
                    sas.map((sa) => (
                      <>
                        <tr key={sa.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                          <td className="px-5 py-3">
                            <button
                              type="button"
                              onClick={() => setExpandedSA(expandedSA === sa.id ? null : sa.id)}
                              className="flex items-center gap-2 text-fg hover:text-accent"
                            >
                              <Bot className="h-4 w-4" />
                              <span className="font-medium">{sa.name}</span>
                            </button>
                          </td>
                          <td className="px-5 py-3 text-fg-muted text-xs">{sa.description || '—'}</td>
                          <td className="px-5 py-3">
                            <Badge tone="neutral">{sa.token_count}</Badge>
                          </td>
                          <td className="px-5 py-3 text-fg-muted text-xs">
                            {sa.created_by}<br />
                            {new Date(sa.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => onDeleteSA(sa.id)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                        {expandedSA === sa.id && (
                          <tr key={`${sa.id}-tokens`}>
                            <td colSpan={5} className="px-5 py-4 bg-bg-inset/40">
                              <SATokenSection projectId={projectId} saId={sa.id} saName={sa.name} />
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                        No service accounts yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function SATokenSection({ projectId, saId, saName }: Readonly<{ projectId: string; saId: string; saName: string }>) {
  const { data: tokens, mutate } = useSATokens(projectId, saId);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['docker:pull']);
  const [expiresIn, setExpiresIn] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedSAToken | null>(null);
  const [copied, setCopied] = useState(false);

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const t = await createSAToken(projectId, saId, {
        name: name.trim(),
        scopes,
        expiresIn: expiresIn || undefined,
      });
      setCreated(t);
      setName(''); setExpiresIn('');
      await mutate();
    } catch (e) {
      alert(`Failed: ${e}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (tokenId: string) => {
    if (!confirm('Revoke this token?')) return;
    await deleteSAToken(projectId, saId, tokenId);
    await mutate();
  };

  const onCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (s: string) => {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  return (
    <div className="space-y-3">
      <p className="orb-section-title text-fg-muted">Tokens for <span className="orb-code">{saName}</span></p>

      {created && (
        <div className="rounded-md border border-success/40 bg-success/5 p-3 space-y-2">
          <p className="text-sm text-fg flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-success" />
            <span className="font-medium">Token created — copy it now</span>
          </p>
          <div className="flex items-center gap-2 p-2 rounded-md bg-bg-inset border border-border font-mono text-xs break-all">
            <Key className="h-3.5 w-3.5 text-success shrink-0" />
            <span className="flex-1">{created.token}</span>
            <button type="button" onClick={onCopy} className="shrink-0 px-2 h-7 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg">
              {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
            </button>
          </div>
          <button type="button" onClick={() => setCreated(null)} className="text-xs text-fg-muted hover:text-fg">
            I've copied it — dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="token name (e.g. github-actions)" />
        <Input value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} placeholder="expires in (e.g. 720h)" />
        <Button variant="secondary" disabled={busy || !name.trim()} onClick={onCreate}>
          <Plus className="h-4 w-4" />
          Mint token
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {SCOPES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => toggleScope(s)}
            className={
              'px-3 h-7 rounded-md text-xs font-mono border ' +
              (scopes.includes(s)
                ? 'border-accent text-accent bg-accent/10'
                : 'border-border text-fg-muted hover:text-fg')
            }
          >
            {s}
          </button>
        ))}
      </div>

      {tokens?.length ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-fg-muted">
              <th className="py-1.5">Name</th>
              <th className="py-1.5">Scopes</th>
              <th className="py-1.5">Created</th>
              <th className="py-1.5">Last used</th>
              <th className="py-1.5">Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-t border-border/40">
                <td className="py-1.5 text-fg">{t.name}</td>
                <td className="py-1.5">
                  {t.scopes.map((s) => (
                    <span key={s} className="orb-code text-[10px] mr-1">{s}</span>
                  ))}
                </td>
                <td className="py-1.5 text-fg-muted">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="py-1.5 text-fg-muted">
                  {t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : '—'}
                </td>
                <td className="py-1.5 text-fg-muted">
                  {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : 'never'}
                </td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(t.id)}
                    className="text-fg-muted hover:text-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-fg-subtle">No tokens yet for this service account.</p>
      )}
    </div>
  );
}
